import dayjs, { Dayjs } from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { ReceptionSlot } from "@/types";

dayjs.extend(isoWeek);

export interface ReceptionGridDay {
  /** ISO date (YYYY-MM-DD). */
  iso: string;
  weekdayLabel: string;
  dayNumber: string;
  isToday: boolean;
  isPast: boolean;
}

export interface ReceptionMonthGridDay extends ReceptionGridDay {
  isCurrentMonth: boolean;
}

/** Monday of the week `d` falls in. */
export const startOfWeek = (d: Dayjs): Dayjs => d.startOf("isoWeek");

/** Mon–Sun day columns for the week starting at `weekStart`, weekday labels in `locale` (e.g. "nb"). */
export function buildWeekDays(weekStart: Dayjs, locale?: string): ReceptionGridDay[] {
  const today = dayjs();
  return Array.from({ length: 7 }, (_, i) => {
    const day = weekStart.add(i, "day");
    return {
      iso: day.format("YYYY-MM-DD"),
      weekdayLabel: (locale ? day.locale(locale) : day).format("ddd"),
      dayNumber: day.format("D"),
      isToday: day.isSame(today, "day"),
      isPast: day.isBefore(today, "day"),
    };
  });
}

/**
 * Full Mon–Sun weeks covering `monthDate`'s month, including the leading/trailing days from the
 * adjacent months needed to fill out the first and last week.
 */
export function buildMonthDays(monthDate: Dayjs): ReceptionMonthGridDay[][] {
  const today = dayjs();
  const month = monthDate.month();
  const year = monthDate.year();
  const gridStart = startOfWeek(monthDate.startOf("month"));
  const gridEnd = startOfWeek(monthDate.endOf("month")).add(6, "day");

  const weeks: ReceptionMonthGridDay[][] = [];
  let cursor = gridStart;
  while (!cursor.isAfter(gridEnd, "day")) {
    weeks.push(
      Array.from({ length: 7 }, (_, i) => {
        const day = cursor.add(i, "day");
        return {
          iso: day.format("YYYY-MM-DD"),
          weekdayLabel: day.format("ddd"),
          dayNumber: day.format("D"),
          isToday: day.isSame(today, "day"),
          isPast: day.isBefore(today, "day"),
          isCurrentMonth: day.month() === month && day.year() === year,
        };
      })
    );
    cursor = cursor.add(7, "day");
  }
  return weeks;
}

/** Groups slots by their `slotDate`. */
export function groupSlotsByDate(slots: ReceptionSlot[]): Map<string, ReceptionSlot[]> {
  const map = new Map<string, ReceptionSlot[]>();
  for (const slot of slots) {
    const existing = map.get(slot.slotDate);
    if (existing) existing.push(slot);
    else map.set(slot.slotDate, [slot]);
  }
  return map;
}

/** Sorted, de-duplicated start times (HH:mm:ss) across the given slots — the grid's row axis. */
export function getVisibleTimeRows(slots: ReceptionSlot[]): string[] {
  return Array.from(new Set(slots.map((slot) => slot.startTime))).sort();
}

export function isSlotPast(slot: ReceptionSlot, now: Dayjs = dayjs()): boolean {
  return dayjs(`${slot.slotDate}T${slot.endTime}`).isBefore(now);
}

export function isSlotFull(slot: ReceptionSlot): boolean {
  return slot.remainingCapacity <= 0;
}

/** Some capacity booked, but not all of it — distinct from fully open (nothing used) or full. */
export function isSlotPartial(slot: ReceptionSlot): boolean {
  return slot.capacityUsed > 0 && slot.remainingCapacity > 0;
}
